import React from 'react';
import { Box, Divider, MenuItem, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CustomSwitch from '../GlobalStyles/CustomSwitch';
import {
  actionMenuMenuListSx,
  getActionMenuListContainerSx,
  getActionMenuPaperSx,
  getActionMenuToggleRowSx,
} from '../../utils/actionMenuStyles';

const HELPER_TEXT_SX = {
  px: 1.75,
  pt: 0.25,
  fontSize: '0.8125rem',
  fontWeight: 400,
  color: 'rgba(0,0,0,0.55)',
  lineHeight: 1.45,
};

const AnalyticsFilterSheet = ({
  open,
  onClose,
  excludeInternal,
  onExcludeInternalChange,
  excludePartnerAgents = false,
  onExcludePartnerAgentsChange,
  partnerAgentEmailDomain = null,
}) => {
  const theme = useTheme();
  const showPartnerToggle = Boolean(partnerAgentEmailDomain);
  const includeInternal = !excludeInternal;
  const includePartnerAgents = !excludePartnerAgents;

  if (!open) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1299,
          background: 'transparent',
        }}
        onClick={onClose}
        aria-hidden="true"
      />
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 56, sm: 64 },
          right: {
            xs: 16,
            sm: 'calc((100vw - min(100vw, 1200px)) / 2 + 24px)',
          },
          width: { xs: 'min(340px, calc(100vw - 16px))', sm: 360 },
          zIndex: 1300,
          '@keyframes analyticsFilterMenuIn': {
            '0%': { opacity: 0, transform: 'translateY(-6px) scale(0.97)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          animation: 'analyticsFilterMenuIn 160ms ease-out both',
          transformOrigin: 'top right',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...getActionMenuPaperSx(theme, 'dropdown'),
            p: 0.75,
          }}
        >
          <Box
            sx={{
              ...getActionMenuListContainerSx('embedded', { uniformSpacing: true }),
              ...actionMenuMenuListSx,
              maxHeight: 'none',
            }}
          >
            <Typography
              sx={{
                ...HELPER_TEXT_SX,
                px: 1.75,
                pb: 0.75,
                pt: 0.5,
                color: 'rgba(0,0,0,0.62)',
              }}
            >
              These filters change visitor and sign-up counts only. Uploads and Recent
              Activity always show everyone.
            </Typography>

            <MenuItem
              sx={getActionMenuToggleRowSx(theme, {
                isFloorPlanDialog: true,
                uniformGutter: true,
              })}
              onClick={(e) => {
                e.stopPropagation();
                onExcludeInternalChange(includeInternal);
              }}
            >
              Include MagicFurnish team
              <CustomSwitch checked={includeInternal} onClick={() => {}} />
            </MenuItem>
            <Typography
              sx={{
                ...HELPER_TEXT_SX,
                pb: showPartnerToggle ? 0.75 : 1,
              }}
            >
              {includeInternal
                ? 'MagicFurnish staff count toward visitor and sign-up totals.'
                : 'MagicFurnish staff are left out of visitor and sign-up totals.'}
            </Typography>

            {showPartnerToggle && (
              <>
                <Divider sx={{ mx: 1.25, my: 0.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                <MenuItem
                  sx={getActionMenuToggleRowSx(theme, {
                    isFloorPlanDialog: true,
                    uniformGutter: true,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExcludePartnerAgentsChange(includePartnerAgents);
                  }}
                >
                  Include your agents
                  <CustomSwitch checked={includePartnerAgents} onClick={() => {}} />
                </MenuItem>
                <Typography
                  sx={{
                    ...HELPER_TEXT_SX,
                    pb: 1,
                  }}
                >
                  {includePartnerAgents
                    ? `Agents on ${partnerAgentEmailDomain} count toward visitor totals. Sign-ups and uploads stay the same.`
                    : `Agents on ${partnerAgentEmailDomain} are left out of visitor totals so you see outside visitors only. Sign-ups and uploads stay the same.`}
                </Typography>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default AnalyticsFilterSheet;
